import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { logSafeAxiosError, reportErrorToUser } from 'src/api/axiosClient/errorHandling';
import { ApiError } from 'src/api/axiosClient/types';
import { useAssetsApi } from 'src/modules/assetLibrary/api/AssetsApi';
import { FILE_UPLOAD_ERROR_MESSAGE } from 'src/constants/messages';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import {
  buildFormData,
  buildRequestData,
  getDefaultApplicationSettings,
} from '../helpers';
import { useApplicationService } from '../services';
import {
  getApplicationSettings,
  setApplication,
  setApplicationSettings,
} from '../slices';
import {
  Application,
  ApplicationFormData,
  ApplicationSettings,
  ApplicationSettingsFormData,
} from '../types';

export interface CreateAdminApplicationFormDrawerController {
  isOpen: boolean;
  initFormData: ApplicationFormData<ApplicationSettingsFormData> | null;
  error: ApiError | null;
  open: (application?: Application) => void;
  close: () => void;
  submit: (
    full: ApplicationFormData<ApplicationSettingsFormData>,
    dirty: Partial<ApplicationFormData<ApplicationSettingsFormData>>,
    nextAction?: () => unknown | Promise<unknown>
  ) => Promise<void>;
}

export const useCreateAdminApplicationFormDrawerController = (): CreateAdminApplicationFormDrawerController => {
  const dispatch = useDispatch();
  const currentApplicationSettings = useSelector(getApplicationSettings);
  const selectedCompanyId = useSelector(getCurrentCompanyId);

  const [initFormData, setInitFormData] = useState<ApplicationFormData<ApplicationSettingsFormData> | null>(null);

  const { createApplication, updateApplication, error } = useApplicationService();

  const { uploadMedia } = useAssetsApi();

  const uploadFile = useCallback(async (file: File): Promise<string> => {
    if (!selectedCompanyId) {
      throw new Error("Cannot upload a file without a selected company");
    }

    const media = await uploadMedia(file, selectedCompanyId);

    return media.url;
  }, [selectedCompanyId, uploadMedia]);

  const open = useCallback((application?: Application) => {
    setInitFormData(buildFormData(application));
  }, []);

  const close = useCallback(() => {
    setInitFormData(null);
  }, []);

  const submit = useCallback(
    async (
      full: ApplicationFormData<ApplicationSettingsFormData>,
      dirty: Partial<ApplicationFormData<ApplicationSettingsFormData>>,
      nextAction?: () => Promise<unknown>
    ) => {
      const { id: applicationId, settings } = full;

      let logo = settings.logo;
      let favicon = settings.favicon;

      try {
        if (typeof logo === "object") {
          logo = await uploadFile(logo);
        }

        if (typeof favicon === "object") {
          favicon = await uploadFile(favicon);
        }
      } catch (e) {
        reportErrorToUser(e, FILE_UPLOAD_ERROR_MESSAGE);
        return;
      }

      const applicationSettings = { ...settings, logo, favicon };
      // What the branding store keeps is the stored shape, not the form's: the two reminder inputs exist only
      // on the form, and `buildRequestData` is the one place that folds them back into a checkpoint list.
      const storedSettings = JSON.parse(
        buildRequestData({ ...full, settings: applicationSettings }).settings,
      ) as ApplicationSettings;

      try {
        if (applicationId) {
          await updateApplication(applicationId, buildRequestData({ ...full, settings: applicationSettings }));

          if (applicationId === currentApplicationSettings?.applicationId) {
            const settings = {
              ...getDefaultApplicationSettings(),
              ...storedSettings,
              name: full.name,
              applicationId,
            };

            dispatch(setApplication({ ...full, settings: JSON.stringify(settings) }));
            dispatch(setApplicationSettings(settings));
          }
        } else {
          await createApplication(buildRequestData({ ...full, settings: applicationSettings }));
        }

        close();
        await nextAction?.();
      } catch (e) {
        // The drawer has no error surface; the interceptor's toast is the only user-facing signal.
        logSafeAxiosError(e);
      }
    },
    [
      uploadFile,
      currentApplicationSettings,
      updateApplication,
      createApplication,
      dispatch,
      close,
    ]
  );

  return {
    isOpen: !!initFormData,
    initFormData,
    error,
    open,
    close,
    submit,
  };
};
